import BluebirdPromise from 'bluebird';
import decode from 'jwt-decode';
import { get, has } from 'lodash';
import { ACTIONS, SEGMENT_EVENTS } from '../Constants';
import AuthService from '../utils/AuthService';
import log from '../utils/log';
import { VerbFetch } from '../VerbFetch';
import { getLocalStorage, postSegmentEvent, setLocalStorage, updateProfileOnLoginSuccess } from './index';
import { hideModal } from './Modal';

function _getUltimatePath(nextPath, profile, company, pathsContext) {
    if (nextPath.nextPath && !nextPath.nextPath.includes('auth/callback')) {
        return nextPath.nextPath;
    }
    if (nextPath.length) {
        // nextPath is almost always an empty string, except when user comes from a custom link i.e /assetlink/{userLearningAssetId}
        return nextPath;
    }
    return '/home';
}

const _loginSuccessFlow = (_loginSuccessParams, history, passThroughParams, dispatch, getState) => {
    const { pathsContext = {}, authContext = {} } = _loginSuccessParams;
    let { nextPath } = _loginSuccessParams;
    const idToken = authContext.token;

    const teamPermalink = passThroughParams.teamPermalink || '';
    const { isAdminRedirect, companyPermalink = '', targetActivity = '', activityPath = '' } = passThroughParams;

    // set values in localstorage
    AuthService.setUserCompany(authContext.companyId);
    AuthService.setUserLanguage('en-us');
    AuthService.setRefreshToken(authContext.token || '');

    // set token here
    const fetchOptions = {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${authContext.token}`
        }
    };

    // Retrieve user profile and set data
    let url = `${pathsContext.serverUrl}/login`;
    let userProfile;
    return VerbFetch(url, fetchOptions)
        .then((body) => {
            const { _id } = body;
            dispatch(hideModal());
            userProfile = body;

            // add profile information to local state here
            dispatch({
                type: ACTIONS.LOGIN_SUCCESS,
                payload: {
                    userProfile: body,
                    idToken
                }
            });
            const loginSuccessParams = {
                id: _id,
                updateParams: { lastSignInDateTime: new Date() },
                pathsContext
            };
            if (teamPermalink) {
                loginSuccessParams.teamPermalink = teamPermalink;
            }

            dispatch(updateProfileOnLoginSuccess(loginSuccessParams));
            log.info('Login Success');
            return body;
        })
        .then((body) => {
            if (body && body.profile) {
                dispatch({
                    type: ACTIONS.UPDATE_PROFILE,
                    payload: {
                        profile: body.profile,
                        isLoggedInUser: true
                    }
                });
            }
            if (body && body.team) {
                nextPath = `/teams/${body.team}`;
            }
            return null;
        })
        .then(() => {
            const companyId = get(userProfile, 'companyId', '');
            const companyObject = getState()?.companies.byId[companyId];

            if (!companyObject._id) {
                //redirect to error page instead
                return history.push('/errorpage');
            }

            if (companyObject && !companyObject.isActive) {
                //redirect to error page instead
                return history.push('/errorpage');
            }

            const permalink = (companyObject && companyObject.permalink) || '';
            if (permalink) {
                // set permalink value in localStorage
                AuthService.setUserCompanyPermalink(permalink);
            }

            const userAuthState = getState().auth;

            const eventToPass = passThroughParams.isNewUser ? SEGMENT_EVENTS.USER_SIGN_UP : SEGMENT_EVENTS.USER_LOG_IN;

            const { isVerbAdmin = false } = userAuthState;

            postSegmentEvent({
                pathsContext,
                body: {
                    userCompany: companyObject,
                    customFields: {
                        isVerbAdmin,
                        isCountedTowardWAU: companyObject.isCountedTowardsWAU || false,
                        currentDateTime: new Date(),
                        companyId: companyObject._id,
                        pushToSegmentEventsCollection: true,
                    },
                    eventTitle: eventToPass
                }
            });

            let path = _getUltimatePath(nextPath, userProfile, companyObject, pathsContext);
            if (isAdminRedirect) {
                path = `/administration`;
                return null;
            }

            if (targetActivity && activityPath) {
                path = `/workspace/${targetActivity}?path=${activityPath}`;
            }
            return history.push(path);
        })
        .catch((err) => {
          console.log({err});
          return history.push('/errorpage');
        });
};

export const loginSuccess = (_loginSuccessParams, history, passThroughParams = {}) => {
    return (dispatch, getState) => {
        return _loginSuccessFlow(_loginSuccessParams, history, passThroughParams, dispatch, getState);
    };
};

export const setToken = (token) => (dispatch) => {
        dispatch({
            type: ACTIONS.LOGIN_RENEW_TOKEN,
            payload: {
                idToken: token
            }
        });
};

export const loginCallback = (loginCallbackParams) => {
    const { history, passThroughParams, pathsContext = {}, authContext = {} } = loginCallbackParams;
    return (dispatch) => {
        let nextPath = getLocalStorage({ key: 'nextPath', dispatchDisabled: true }) || '';

        if (passThroughParams.nextPath) {
            nextPath = { nextPath : passThroughParams.nextPath};
        }

        window.localStorage.removeItem('nextPath');
        const params = {
            nextPath,
            pathsContext,
            authContext
        };
        return dispatch(loginSuccess(params, history, passThroughParams));
    };
};

export const clearSource = () => (dispatch) =>
    dispatch({
        type: ACTIONS.CLEAR_SOURCE
    });
